import { CalendarDays } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import {
  DARK_BLUE_BAND_CLASS,
  DARK_BLUE_BAND_MUTED_TEXT_CLASS,
  DARK_BLUE_BAND_SKELETON_CLASS,
} from '@/styles/darkBlueBand';
import { pluralize } from '@/utils/number';
import {
  MONTH_DAY_KIND_LABEL,
  MONTH_DAY_KINDS,
  type MonthDayKind,
} from '../constants/holiday.constants';

interface HolidayMonthSummaryProps {
  monthLabel: string;
  dayCounts: Record<MonthDayKind, number> | undefined;
}

export function HolidayMonthSummary({ monthLabel, dayCounts }: HolidayMonthSummaryProps) {
  const totalDays = dayCounts
    ? MONTH_DAY_KINDS.reduce((total, kind) => total + dayCounts[kind], 0)
    : undefined;

  return (
    <div
      className={cn(
        'flex min-h-9 flex-wrap items-center justify-between gap-x-6 gap-y-1 border-b border-primary/10 px-4 py-1',
        DARK_BLUE_BAND_CLASS,
      )}
    >
      <p className={cn('flex items-center gap-1.5 text-sm', DARK_BLUE_BAND_MUTED_TEXT_CLASS)}>
        <CalendarDays className="size-4 text-white" aria-hidden />
        {monthLabel}
        {totalDays === undefined ? null : (
          <span className="ml-0.5 font-semibold text-white tabular-nums">
            {totalDays} {pluralize(totalDays, 'day')}
          </span>
        )}
      </p>
      <ul className="flex flex-wrap items-center gap-x-5 gap-y-1.5">
        {MONTH_DAY_KINDS.map((kind) => (
          <li key={kind} className="flex items-center gap-1.5 text-sm">
            <span className={DARK_BLUE_BAND_MUTED_TEXT_CLASS}>{MONTH_DAY_KIND_LABEL[kind]}:</span>
            {dayCounts ? (
              <span className="font-semibold tabular-nums">{dayCounts[kind]}</span>
            ) : (
              <Skeleton className={cn('h-4 w-5', DARK_BLUE_BAND_SKELETON_CLASS)} />
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
